"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";
import { useYouTubeActions } from "@/lib/hooks/useYouTube";
import { toast } from "@/components/ui/use-toast";
import { Loader2, ShieldAlert, AlertCircle, Youtube } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

function isPermissionError(err: unknown): boolean {
  const code = (err as any)?.code;
  if (code === "permission_denied") return true;
  const msg = ((err as any)?.message || String(err)).toLowerCase();
  return (
    msg.includes("access_denied") ||
    msg.includes("permission") ||
    msg.includes("scope") ||
    msg.includes("denied") ||
    msg.includes("not authorized") ||
    msg.includes("authorization") ||
    msg.includes("insufficient")
  );
}

export default function YouTubeCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useLanguageNavigate();
  const youtube = useYouTubeActions();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isPermission, setIsPermission] = useState(false);

  const REQUIRED_SCOPES = [
    "https://www.googleapis.com/auth/youtube.upload",
    "https://www.googleapis.com/auth/youtube.readonly",
  ];

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");
    const scopeParam = searchParams.get("scope");

    if (errorParam) {
      const checkStr = errorParam + " " + (searchParams.get("error_description") || "");
      const rawErr = { message: checkStr };
      const permErr = isPermissionError(rawErr);
      setIsPermission(permErr);
      if (permErr) {
        setError(t("youtube.insufficientPermissions"));
      } else {
        setError(searchParams.get("error_description") || t("youtube.oauthFailed"));
        setTimeout(() => navigate("/account"), 4000);
      }
      return;
    }

    // Check if all required scopes were granted (Google includes scope in redirect URL)
    if (scopeParam) {
      const granted = scopeParam.split(" ").map((s) => s.trim());
      const missing = REQUIRED_SCOPES.filter((s) => !granted.includes(s));
      if (missing.length > 0) {
        setIsPermission(true);
        setError(t("youtube.insufficientPermissions"));
        return;
      }
    }

    if (!code || !state) {
      setError(t("youtube.invalidCallback"));
      setTimeout(() => navigate("/account"), 3000);
      return;
    }

    youtube
      .handleCallback(code, state)
      .then((result) => {
        toast({
          title: t("youtube.connected"),
          description: t("youtube.connectedDesc", { channelTitle: result.channelTitle }),
        });
        navigate("/account");
      })
      .catch((err) => {
        const permErr = isPermissionError(err);
        setIsPermission(permErr);
        if (permErr) {
          setError(t("youtube.insufficientPermissions"));
        } else {
          setError((err as Error).message || t("youtube.oauthFailed"));
          setTimeout(() => navigate("/account"), 4000);
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = async () => {
    setError(null);
    setIsPermission(false);
    try {
      const { authUrl } = await youtube.getAuthUrl();
      window.location.href = authUrl;
    } catch {
      navigate("/account");
    }
  };

  return (
    <div className="min-h-screen bg-lavender flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-8 max-w-md w-full flex flex-col items-center gap-6 text-center">
        {error ? (
          isPermission ? (
            <>
              <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200">
                <ShieldAlert className="w-10 h-10 text-amber-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-bold">{t("youtube.permissionDeniedTitle")}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{error}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button
                  onClick={handleRetry}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg h-11"
                >
                  <Youtube className="w-4 h-4 mr-2" />
                  {t("youtube.tryAgain")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/account")}
                  className="flex-1 rounded-lg h-11"
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 rounded-2xl bg-red-50 border border-red-200">
                <AlertCircle className="w-10 h-10 text-destructive" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-bold text-destructive">{t("youtube.oauthFailed")}</h2>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <p className="text-xs text-muted-foreground">{t("youtube.redirecting")}</p>
            </>
          )
        ) : (
          <>
            <div className="p-4 rounded-2xl bg-red-50 border border-red-200">
              <Loader2 className="w-10 h-10 animate-spin text-red-500" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-bold">{t("youtube.connectAccount")}</h2>
              <p className="text-sm text-muted-foreground">{t("youtube.connecting")}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
